import { Routes, Route } from "react-router-dom";
import { Workflow, GitBranch, Database, DollarSign } from "lucide-react";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import NebulaBackground from "./components/NebulaBackground";
import Overview from "./pages/Overview";
import AIStack from "./pages/AIStack";
import CodeGalaxy from "./pages/CodeGalaxy";
import Services from "./pages/Services";
import Topology from "./pages/Topology";
import Health from "./pages/Health";
import SiteMetrics from "./pages/SiteMetrics";
import Chat from "./pages/Chat";
import Stub from "./pages/Stub";

export default function App() {
  return (
    <div className="min-h-screen flex">
      <NebulaBackground />
      <Sidebar />
      <main className="flex-1 min-w-0">
        <TopBar />
        <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto">
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/ai-stack" element={<AIStack />} />
            <Route path="/repos" element={<CodeGalaxy />} />
            <Route path="/services" element={<Services />} />
            <Route path="/topology" element={<Topology />} />
            <Route path="/health" element={<Health />} />
            <Route path="/site-metrics" element={<SiteMetrics />} />
            <Route path="/chat" element={<Chat />} />

            <Route path="/n8n" element={
              <Stub
                title="n8n Workflows"
                subtitle="Layer · Automation"
                icon={Workflow}
                body="39 workflows defined; 1 verified-working today. The blocking issues are 2 Postgres credential refs (need Supabase password + SSL=Require) and 4 OAuth flows (MSGraph, Gmail, IMAP, Atlassian) that require browser consent — these are user-only fixes."
                links={[
                  { label: "Open n8n.willbracken.com", href: "https://n8n.willbracken.com" },
                  { label: "View workflows JSON", href: "https://github.com/BrackenW3/n8n" },
                ]}
                upcoming={[
                  "Workflow status grid (39 cards by category: production / setup / sub-workflow / test)",
                  "Per-workflow last-run + duration + error rate from Supabase ai_usage_log",
                  "One-click trigger for safe workflows; gated for destructive ones",
                  "Failure history sparkline per workflow",
                ]}
              />
            }/>
            <Route path="/atlassian" element={
              <Stub
                title="Atlassian"
                subtitle="Layer · Family Operations"
                icon={GitBranch}
                body="willbracken.atlassian.net hosts 5 projects (KAN, MPB, PM, SAM1, TAS). Family-facing project must remain agile-free: no sprints, no story points, no burndown. Personal accounts must NEVER appear on the family board except for chore assignments."
                links={[
                  { label: "Open willbracken.atlassian.net", href: "https://willbracken.atlassian.net" },
                  { label: "Family Confluence home", href: "https://willbracken.atlassian.net/wiki" },
                ]}
                upcoming={[
                  "Live Jira project board with task counts per project",
                  "Confluence calendar widget embed (the missing piece for parent buy-in)",
                  "Word→Excel converter widget for Dad's weekly Task List",
                  "Family bot status (the existing custom bots stay untouched)",
                ]}
              />
            }/>
            <Route path="/data" element={
              <Stub
                title="Data Layer"
                subtitle="Layer · Storage & Search"
                icon={Database}
                body="Supabase Postgres 17.6 holds 35,710 docs / 34,977 embedded (97.9%). Neo4j on Railway is currently broken (404 — domain rotated). Local Postgres + pgvector planned for the i7-9700K NAS box once data is preserved."
                links={[
                  { label: "Supabase project", href: "https://supabase.com/dashboard/project/smttdhtpwkowcyatoztb" },
                  { label: "Neo4j (broken)",   href: "https://neo4j-production-dafa.up.railway.app:7474" },
                ]}
                upcoming={[
                  "Live document count + embedding-coverage gauge",
                  "Top tables by row count, top views by query frequency",
                  "Pgvector search demo (semantic over the 34,977 docs)",
                  "Neo4j graph view once Railway URL is fixed",
                ]}
              />
            }/>
            <Route path="/cost" element={
              <Stub
                title="Cost"
                subtitle="Layer · FinOps"
                icon={DollarSign}
                body="Monthly target: $30 lean / $60 comfortable. Current burn: Railway $10–17, OpenRouter metered, Anthropic Pro, Codex Pro, Z.AI Pro (locked-in cheap), Azure $117 credit expiring May 8. The historic $1000+/mo was Anthropic API metered usage on top of Pro — eliminated by capping spend in OpenRouter."
                upcoming={[
                  "Live cost chart by provider (LangFuse trace data)",
                  "Hard monthly cap per provider with auto-cutoff",
                  "Daily Azure burn with credit-runway forecast",
                  "Per-agent cost attribution (which ai-router agent burns the most)",
                ]}
              />
            }/>
          </Routes>
        </div>
      </main>
    </div>
  );
}
